import {
  EmailTemplateTypes,
  IContactPerson,
  PolicyType,
  StatementRelativePeriods,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";
import { When } from "react-if";

import { useAdhocActionModal } from "../../../../hooks/useAdhocActionModal";
import { Heading } from "../../../../../common/Atoms/Typography/Heading";
import { EmailTemplateSelector } from "../PolicyForm/Fields/EmailTemplateSelector";
import { Select } from "../../../../../common/Atoms/Select";
import { MultiSelect } from "../../../../../common/Atoms/MultiSelect";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { Badge } from "../../../../../common/Atoms/Badge";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { SenderProfileSelector } from "../PolicyForm/SenderProfileSelector";
import { StatementTemplateSelector } from "../PolicyForm/Fields/StatementTemplateSelector";

function contactPersonLabel(person: IContactPerson) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(` `);

  if (name && person.email) return `${name} (${person.email})`;

  return name || person.email || `Unnamed person`;
}

const periodOptions = [
  {
    label: `All Outstanding Items`,
    value: StatementRelativePeriods.ALL_TIME,
  },
  {
    label: `View all outstanding & future`,
    value: StatementRelativePeriods.ALL_TIME_INC_FUTURE,
  },
  {
    label: `Issued before current month`,
    value: StatementRelativePeriods.UPTO_PREVIOUS_MONTH,
  },
  {
    label: `Issued only in the previous month`,
    value: StatementRelativePeriods.PREVIOUS_MONTH,
  },
  {
    label: `Issued only in the current month`,
    value: StatementRelativePeriods.THIS_MONTH,
  },
];

export function AdhocStatement() {
  const currentOrg = useGetSelectedOrganisation();
  const {
    type,
    currentSystemPolicy,
    currentSystemPolicyForm,
    update,
    contactName,
    contactId,
    contactIds,
    isBulk,
    isAnnouncement,
    contactPersonIds,
    setContactPersonIds,
    adhocContactPersons,
    adhocContactLoading,
    noContactPersonsSelected,
    showPersonSelect,
  } = useAdhocActionModal();

  if (!currentSystemPolicy || !currentSystemPolicyForm) return null;

  if (!type) return null;

  if (type !== PolicyType.STATEMENT || currentSystemPolicy.policy_type !== PolicyType.STATEMENT) return null;

  if (!contactIds && !contactId) return null;

  const thingBeingSent = isAnnouncement ? `announcement` : `statement`;

  const headingText = isBulk ?
    `Send one off ${thingBeingSent} for ${contactIds.length} contacts` :
    `Send a one off ${thingBeingSent} to ${contactName || `this contact`}`;

  const personOptions = adhocContactPersons.map(person => {
    const eligible = (person.isPrimary || person.includeInNotifications) && !!person.email;

    return {
      label: contactPersonLabel(person),
      value: person.id as string,
      badge: person.isPrimary ? `Primary` : undefined,
      disabled: !eligible,
      tooltip: eligible ?
        undefined :
        (person.email ? `Excluded from emails` : `No email address`),
    };
  });

  const selectedCount = contactPersonIds?.length || 0;

  const personDisplay = noContactPersonsSelected ?
    `Select contact persons` :
    (selectedCount >= personOptions.length ? `All contact persons` : `${selectedCount} of ${personOptions.length} selected`);

  return (
    <div>
      <Heading>
        { headingText }
      </Heading>

      <div className={ `p-4 space-y-2` }>
        <EmailTemplateSelector
          value={ currentSystemPolicyForm.template_id }
          name={ `template_id` }
          handleChange={ update }
          label={ `Email template` }
          emptyText={ `Select Template` }
          nullable
          currentPolicyId={ currentSystemPolicy.id }
          type={ isAnnouncement ? EmailTemplateTypes.ANNOUNCEMENT : EmailTemplateTypes.STATEMENT_EMAIL }
        />

        <When condition={ showPersonSelect }>
          <MultiSelect
            label={ `Send to` }
            options={ personOptions }
            selected={ contactPersonIds || [] }
            onChange={ value => setContactPersonIds(value as string[]) }
            display={ personDisplay }
            loading={ adhocContactLoading }
            warning={ noContactPersonsSelected ? `Select at least one contact person` : undefined }
            helpIcon={ `Choose which contact persons receive this ${thingBeingSent}. Defaults to everyone included in emails.` }
          />
        </When>

        <When condition={ !isAnnouncement }>
          <Select
            label={ `Outstanding Statement Period` }
            options={ periodOptions }
            onChange={ update }
            selected={ currentSystemPolicyForm.statement_period }
            name={ `statement_period` }
            helpIcon={
              `Only applies to outstanding statements. Which Invoices & Credits to include in the statement. \n Current and previous months are relative to the day in which the statement is sent. Based on Issue Date.`
            }
          />

          <Toggle
            label={ `Attach outstanding statement as PDF` }
            checked={ currentSystemPolicyForm.include_statement_pdf || false }
            onChange={ update }
            name={ `include_statement_pdf` }
            description={ `Attaches a generated PDF outstanding statement to the email` }
          />

          <When condition={ currentSystemPolicyForm.include_statement_pdf }>
            <StatementTemplateSelector
              policy={ currentSystemPolicyForm }
              handleChange={ update }
            />
          </When>

          <When condition={ accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT) }>
            <Toggle
              label={ (
                <span className={ `flex` }>
                  { `Attach activity statement as PDF` }
                  <Badge
                    color={ `purple` }
                    className={ `ml-1.5` }
                  >
                    { `New` }
                  </Badge>

                </span>
              ) }
              checked={ currentSystemPolicyForm.include_activity_pdf || false }
              onChange={ update }
              name={ `include_activity_pdf` }
              description={ `Attaches a generated PDF activity statement to the email using the default date period.` }
              helpIcon={ `You can edit the default date period in organisation settings. Ability to override the dates here is coming soon!` }
            />
          </When>
        </When>

        <SenderProfileSelector
          handleChange={ update }
          policyFormData={ currentSystemPolicyForm }
          invalids={ [] }
          anchor={ `top` }
        />
      </div>
    </div>
  );
}
